/**
 * Пиктограмма упражнения: фигурка, которая показывает движение.
 *
 * Сами позы и геометрия — в `figures/` (чистый код без React). Здесь только
 * отрисовка: элементы SVG и `<animate>` на те атрибуты, что меняются по кадрам.
 * Анимацию ведёт браузер, без JS на кадр.
 *
 * ДВА ОГРАНИЧИТЕЛЯ, и оба не про красоту:
 *   - фигурка вне экрана ставится на паузу: на «Сегодня» их семь, в выборе
 *     упражнения — сорок, и крутить те, что не видны, значит греть телефон;
 *   - при «уменьшить движение» в настройках системы фигурка неподвижна и
 *     показывает самое узнаваемое положение (`still` в описании позы).
 */

import { createElement, useEffect, useMemo, useRef, useState } from 'react'

import { FIGURES } from '../figures/catalog'
import { buildFigure, type FigureModel } from '../figures/engine'

/** Имена атрибутов SVG, которые React ждёт в другой записи. */
const REACT_ATTR: Record<string, string> = { 'stroke-width': 'strokeWidth' }

const models = new Map<string, FigureModel>()

function modelOf(exerciseId: number, animated: boolean): FigureModel | null {
  const spec = FIGURES[exerciseId]
  if (!spec) return null
  const key = `${exerciseId}:${animated ? 'a' : 's'}`
  let model = models.get(key)
  if (!model) {
    model = buildFigure(spec, animated)
    models.set(key, model)
  }
  return model
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReduced(query.matches)
    query.addEventListener?.('change', update)
    return () => query.removeEventListener?.('change', update)
  }, [])
  return reduced
}

export function ExerciseFigure({
  exerciseId,
  animated = true,
  freeze,
}: {
  exerciseId: number
  animated?: boolean
  /** Показать неподвижно на доле пути 0..1 — для галереи поз. */
  freeze?: number
}) {
  const reduced = usePrefersReducedMotion()
  const moving = animated && !reduced && freeze === undefined
  const ref = useRef<SVGSVGElement>(null)

  const model = useMemo(() => {
    if (freeze === undefined) return modelOf(exerciseId, moving)
    const spec = FIGURES[exerciseId]
    return spec ? buildFigure({ ...spec, still: freeze }, false) : null
  }, [exerciseId, moving, freeze])

  // Вне экрана анимация стоит. Корень наблюдения — окно: пересечение и так
  // считается с учётом всех прокручиваемых предков.
  useEffect(() => {
    const svg = ref.current
    if (!svg || !model || model.keyTimes.length === 0) return
    if (typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) svg.unpauseAnimations()
        else svg.pauseAnimations()
      }
    })
    observer.observe(svg)
    return () => observer.disconnect()
  }, [model])

  if (!model) return null

  const keyTimes = model.keyTimes.map((t) => Number(t.toFixed(4))).join(';')

  return (
    <svg ref={ref} className="fg" viewBox={model.viewBox} aria-hidden="true" focusable="false">
      {model.shapes.map((shape, index) => {
        const props: Record<string, unknown> = { key: index, className: shape.cls }
        for (const [name, value] of Object.entries(shape.attrs)) props[REACT_ATTR[name] ?? name] = value
        const animations = Object.entries(shape.frames).map(([name, values]) => {
          // Первый кадр — ещё и обычный атрибут: без него элемент пуст, пока
          // анимация не стартовала (и навсегда, если SMIL в браузере выключен).
          props[REACT_ATTR[name] ?? name] = values[0]
          return (
            <animate
              key={name}
              attributeName={name}
              values={values.join(';')}
              keyTimes={keyTimes}
              dur={`${model.duration}s`}
              repeatCount="indefinite"
            />
          )
        })
        return createElement(shape.tag, props, ...animations)
      })}
    </svg>
  )
}
